import React, { useState } from 'react';
import './SourcesButton.css';

/**
 * 출처 버튼 컴포넌트 (답변 아래 표시)
 * - 사이트 favicon + 도메인 + 페이지 경로 표시 (GPT/Claude/Gemini 공통)
 * - Gemini는 vertexaisearch redirect URL이 url에 들어오므로 title을 도메인으로 사용
 */

const FALLBACK_FAVICON =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%239ca3af" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>';

/**
 * source에서 사람이 보기 좋은 도메인을 추출.
 * - vertexaisearch redirect URL은 hostname이 vertexaisearch... 이므로 title을 우선 사용
 *   (Gemini는 grounding_chunks[].web.title을 보통 site domain으로 채움)
 * - 그 외엔 hostname에서 www. 제거
 */
function getDisplayDomain(source) {
  const rawTitle = (source?.title || '').trim();
  const rawUrl = source?.url || '';

  let urlHost = '';
  try {
    urlHost = new URL(rawUrl).hostname.replace(/^www\./, '');
  } catch (e) {
    urlHost = '';
  }

  const isVertexRedirect = urlHost.includes('vertexaisearch.cloud.google.com');

  if (isVertexRedirect) {
    if (rawTitle && /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(rawTitle)) {
      return rawTitle.replace(/^www\./, '');
    }
    return rawTitle || urlHost || '출처';
  }

  if (urlHost) return urlHost;
  return rawTitle || '출처';
}

/**
 * source에서 페이지 경로 (도메인 뒤 path) 추출. 길면 잘라서 보여줌.
 */
function getDisplayPath(source) {
  const rawUrl = source?.url || '';
  try {
    const u = new URL(rawUrl);
    if (u.hostname.includes('vertexaisearch.cloud.google.com')) {
      return '';
    }
    let path = u.pathname || '';
    if (u.search) path += u.search;
    path = decodeURIComponent(path).replace(/^\/+/, '');
    if (!path || path === '/') return '';
    if (path.length > 60) path = path.slice(0, 57) + '…';
    return path;
  } catch (e) {
    return '';
  }
}

function FaviconImg({ domain }) {
  const [errored, setErrored] = useState(false);
  if (!domain || errored) {
    return (
      <img
        src={FALLBACK_FAVICON}
        alt=""
        className="sources-favicon"
        width={16}
        height={16}
      />
    );
  }
  const src = `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64`;
  return (
    <img
      src={src}
      alt=""
      className="sources-favicon"
      width={16}
      height={16}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setErrored(true)}
    />
  );
}

const SourcesButton = ({ sources = [], label = '답변 근거', onOpen, onSourceClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!sources || sources.length === 0) {
    return null;
  }

  const validSources = sources.filter((s) => s && s.url);

  if (validSources.length === 0) {
    return null;
  }

  return (
    <div className="sources-button-container">
      <button
        className="sources-button"
        onClick={() => {
          if (!isExpanded) onOpen?.(validSources.length);
          setIsExpanded(!isExpanded);
        }}
        type="button"
        aria-expanded={isExpanded}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span>{label} {validSources.length}개</span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          className={`sources-button-arrow ${isExpanded ? 'expanded' : ''}`}
        >
          <path
            d="M19 9l-7 7-7-7"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isExpanded && (
        <div className="sources-list">
          {validSources.map((source, index) => {
            const domain = getDisplayDomain(source);
            const path = getDisplayPath(source);
            const title = (source.title || '').trim();
            const snippet = (source.snippet || source.description || '').trim();
            return (
              <a
                key={index}
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="sources-list-item"
                onClick={() => onSourceClick?.(validSources.length)}
                title={source.url}
              >
                <FaviconImg domain={domain} />
                <div className="sources-list-text">
                  <span className="sources-list-domain">{title && title !== domain ? title : domain}</span>
                  <span className="sources-list-url">{domain}{path ? `/${path}` : ''}</span>
                  {snippet && <span className="sources-list-snippet">{snippet}</span>}
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SourcesButton;
